import { z } from "zod";

// Web push (S7.04, adrs/001-web-push.md): one subscription per device. A
// device names itself with a `deviceId` it keeps in `localStorage` (D087),
// so re-subscribing the same browser replaces its row instead of adding a
// second one. `p256dh`/`auth` are the subscription's own public key and
// auth secret (RFC 8291); the server never sees its private key.

export const PUSH_USER_AGENT_LABEL_MAX = 160;

export const pushSubscriptionKeysSchema = z.object({
  p256dh: z.string().min(1),
  auth: z.string().min(1),
});
export type PushSubscriptionKeys = z.infer<typeof pushSubscriptionKeysSchema>;

export const subscribeInputSchema = z.object({
  deviceId: z.string().min(1).max(100),
  endpoint: z.url(),
  keys: pushSubscriptionKeysSchema,
  userAgentLabel: z.string().max(PUSH_USER_AGENT_LABEL_MAX).optional(),
});
export type SubscribeInput = z.infer<typeof subscribeInputSchema>;

export const unsubscribeInputSchema = z.object({
  deviceId: z.string().min(1).max(100),
});
export type UnsubscribeInput = z.infer<typeof unsubscribeInputSchema>;

export const unsubscribeOutputSchema = z.object({ removed: z.boolean() });
export type UnsubscribeOutput = z.infer<typeof unsubscribeOutputSchema>;

/** What the settings screen's "this device" control reads; never the raw endpoint or keys. */
export const pushSubscriptionSummarySchema = z.object({
  deviceId: z.string(),
  userAgentLabel: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type PushSubscriptionSummary = z.infer<typeof pushSubscriptionSummarySchema>;

export const vapidPublicKeySchema = z.object({ key: z.string() });
export type VapidPublicKey = z.infer<typeof vapidPublicKeySchema>;

export const emptyInputSchema = z.object({});

/** What a peer raises a push with; never on the wire, the same way notification's own `notify` is. */
export const notifyInputSchema = z.object({
  userId: z.string(),
  title: z.string().min(1),
  body: z.string().nullable(),
  noticeId: z.string(),
  type: z.string(),
});
export type NotifyInput = z.infer<typeof notifyInputSchema>;
